import { Utils, MINIMUM_CANDLE_SIZE, MAXIMUM_CANDLE_SIZE_CHARTS } from "@app/_helpers";
import { SymbolSearchService, QuoteStoreService, ConfigurationService, QUOTE_UPDATE_TYPE, MarketSocketService } from "@app/_shared/providers";
import { ChartingService } from "@app/_shared/providers/charting/charting.service";
import { MarketIDMap, CandleIntervals, MarketIDRevMap } from "@app/_shared/shared.enums";
import { MarketWatch } from "@app/app.models";
import { chartInstrument, Instrument } from "@app/app.structs";
import { MarketWatchService } from "@app/modules/trading/modules/market-watch/providers/market-watch/market-watch.service";
import { Subscription } from "rxjs";
import { ChartingFilters, CandleUpdateObj, ChartDataResponse } from "./chart.interface";
import { ChartIntervals } from "./chart.structs";
import { IExternalDatafeed, IDatafeedQuotesApi, IDatafeedChartApi, Bar, OnReadyCallback, ResolutionString, SearchSymbolResultItem, LibrarySymbolInfo, MarketSessionTimings, PeriodParams, HistoryMetadata, QuoteOkData, QuoteErrorData } from "./tradingview.lib.structs";
import { ChartUtilities } from "@app/_helpers/chart-utilities";


export class TradingViewDatafeed implements IExternalDatafeed, IDatafeedQuotesApi, IDatafeedChartApi {
    chartingDataService: ChartingService;
    symbolSearchService: SymbolSearchService;
    selectedInstrument: MarketWatch;
    marketService: MarketSocketService;
    marketWatchService: MarketWatchService;
    quoteService: QuoteStoreService;
    remoteConfigService: ConfigurationService;
    responseId: number;
    // _sockets: SocketService;
    private _socketSubscriptionMap: Map<string, Subscription> = new Map();
    public onResetCacheCallback;
    unsuccessfulTries = {
        count: {},
        fromDate: {},
        toDate: {},
    };
    firstVolumeDict = {};
    symbolIdResponseId = new Map();
    private ohlcCallBackMap: Map<number, any> = new Map();
    resolutuon;
    latestWeeklyMonthlyCandle: Bar;
    latestDailyCandle: Bar;

    parseResolutions = {
        '1': ChartIntervals['1m'],
        '5': ChartIntervals['5m'],
        '10': ChartIntervals['10m'],
        '15': ChartIntervals['15m'],
        '30': ChartIntervals['30m'],
        '60': ChartIntervals['1h'],
        '240': ChartIntervals['4h'],
        'D': ChartIntervals['1d'],
        '1D': ChartIntervals['1d'],
        '1W': ChartIntervals['1W'],
        'W': ChartIntervals['1W'],
        '1M': ChartIntervals['1M'],
        'M': ChartIntervals['1M']
    };

    constructor(
        chartingDataService: ChartingService,
        symbolSearchService: SymbolSearchService,
        quoteService: QuoteStoreService,
        marketService: MarketSocketService,
        marketWatchService: MarketWatchService,
        remoteConfigService: ConfigurationService,
    ) {

        this.chartingDataService = chartingDataService;
        this.symbolSearchService = symbolSearchService;
        this.remoteConfigService = remoteConfigService;
        this.marketService = marketService;
        this.marketWatchService = marketWatchService;
        this.quoteService = quoteService;

        // Handles the historical data recived from MDL
        this.quoteService.getFeedSubject(QUOTE_UPDATE_TYPE.OHLC).subscribe(resData => {
            const ohlcData = this.ohlcCallBackMap.get(resData.responseId);
            if (!ohlcData) {
                return;
            }

            let onResult = ohlcData.onResult;
            let symbolInfo = ohlcData.symbolInfo;
            let periodParams = ohlcData.periodParams;
            let resolution = ohlcData.resolution;
            const res = resData.data;
            const allBars: Bar[] = [];

            for (let resultIdx = 0; resultIdx < res.length; resultIdx++) {
                allBars[resultIdx] = {
                    open: parseFloat(res[resultIdx].open_price),
                    close: parseFloat(res[resultIdx].close_price),
                    volume: parseFloat(res[resultIdx].volume),
                    time: Utils.istToGmt(res[resultIdx].timestamp),
                    high: parseFloat(res[resultIdx].high_price),
                    low: parseFloat(res[resultIdx].low_price)
                };
            }

            if (res && !res.length) {
                // For initial request, adding symbol to retry count such that retries work for default interval
                if (!(symbolInfo.name in this.unsuccessfulTries.count)) {
                    this.unsuccessfulTries.count[symbolInfo.name] = 0;
                }
                if (this.unsuccessfulTries.count[symbolInfo.name] < 10) {
                    this.unsuccessfulTries.count[symbolInfo.name]++;
                    // console.log("On Retrying: " + resData.responseId);
                    // console.log(periodParams);
                    onResult(allBars, {
                        noData: false,
                        nextTime: ++this.unsuccessfulTries.fromDate[symbolInfo.name]
                    });
                } else {
                    this.unsuccessfulTries.count[symbolInfo.name] = 0;
                    // console.log("On Exhaustung retries: " + resData.responseId);
                    // console.log(periodParams);
                    onResult(allBars, { noData: true });
                }
            } else {
                this.unsuccessfulTries.count[symbolInfo.name] = 0;
                // console.log("On Data Recived: " + resData.responseId);
                // console.log(periodParams);
                let newBars = allBars;
                if (resolution.endsWith('D')) {
                    // Store the last added day candle
                    this.latestDailyCandle = {
                        open: parseFloat(allBars[allBars.length - 1].open.toString()),
                        close: parseFloat(allBars[allBars.length - 1].close.toString()),
                        volume: parseFloat(allBars[allBars.length - 1].volume.toString()),
                        time: allBars[allBars.length - 1].time,
                        high: parseFloat(allBars[allBars.length - 1].high.toString()),
                        low: parseFloat(allBars[allBars.length - 1].low.toString())
                    };
                }
                else if (resolution.endsWith('W') || resolution.endsWith('M')) {
                    // create candles for weekly and monthly ohlc data with daily ohlc data
                    const marketHours = ChartUtilities.defaultMarketMap(this.remoteConfigService.remoteConfigCacheObj['ChartMarketHours']);
                    const marketRules = ChartUtilities.processMarketRules(marketHours[symbolInfo.exchange.toUpperCase()]);
                    const holidays = marketRules.session_holidays;
                    const resolutiontype = resolution.endsWith('W') ? 'weekly' : 'monthly';
                    newBars = this.aggregateCandles(allBars, resolutiontype, holidays);
                }
                onResult(newBars, { noData: false });
            }
            this.ohlcCallBackMap.delete(resData.responseId);
        }, err => {
            // let onError = this.ohlcCallBackMap[resData.responseId].onError;
            // onError(err.error);
        });
    }

    public onReady(callback: OnReadyCallback): void {
        setTimeout(() => {
            callback({
                exchanges: [],
                supported_resolutions: ['1', '5','10', '15', '30', '60', '1D', '1W', '1M'] as ResolutionString[],
                supports_marks: true,
                supports_timescale_marks: false,
                symbols_types: [],
                supports_time: false
            });
        }, 0);
    }

    public updateInstrument(selectedInstrument: MarketWatch) {
        // Previous unsuccessful tries data should be clear on symbol change.
        if (this.selectedInstrument && this.unsuccessfulTries.count[this.selectedInstrument.scrip_name]) {
            delete this.unsuccessfulTries.count[this.selectedInstrument.symbol];
            delete this.unsuccessfulTries.toDate[this.selectedInstrument.symbol];
            delete this.unsuccessfulTries.fromDate[this.selectedInstrument.symbol];
            delete this.firstVolumeDict[this.selectedInstrument.symbol_id];
        }
        this.selectedInstrument = selectedInstrument;
        this.unsuccessfulTries.count[this.selectedInstrument.symbol] = 0;
    }

    searchSymbols(userInput: string, exchange: string, symbolType: string, onResult: (items: SearchSymbolResultItem[]) => void): void {
        const searchedSymbols = this.symbolSearchService.search(userInput, false, true);

        const results = [];
        if (searchedSymbols.length) {

            searchedSymbols.forEach((symbol) => {
                // Setting market id and security id in search result objects, to be able to request for
                // symbol details in resolve symbol method.
                results.push({
                    symbol: symbol.scrip_name,
                    market_id: symbol.market_id,
                    security_id: symbol.security_id,
                    full_name: symbol.scrip_name,
                    type: Instrument[symbol.instrument_type],
                    exchange: symbol.market_id,
                    description: symbol.scrip_name,
                    ticker: `${symbol.market_id} ${symbol.security_id} ${symbol.instrument_type}`
                });
            });
        }
        onResult(results);
    }


    resolveSymbol(symbolName: string, onResolve: (symbolInfo: LibrarySymbolInfo) => void, onError: (reason: string) => void): void {
        setTimeout(() => {
            // Below condition means that the symbol is requested via compare/add symbol widget.
            // In compare symbol request, symbolName Format:- market_id security_id instrumnet_type. Sometimes, this method
            // is called two times in which only symbol_id comes as a string so handled that in else clause.
            if (symbolName !== this.selectedInstrument.symbol && this.symbolSearchService.getFormattedSymbol(Utils.getJoinedString(this.selectedInstrument.market_id, this.selectedInstrument.security_id)) !== symbolName) {
                let searchFilter = {};
                searchFilter = {
                    market_id: MarketIDMap[symbolName.split(' ')[0]],
                    security_id: parseInt(symbolName.split(' ')[1], 10),
                    is_index: (symbolName.split(' ')[2]) === Instrument.Index
                };

                this.marketWatchService.getMWStaticData(searchFilter).subscribe((response: any) => {
                    const symbolDetails = response;
                    Object.keys(symbolDetails).forEach(key => {
                        this.selectedInstrument[key] = symbolDetails[key];
                    });
                    // this.selectedInstrument.copyKeysFromObject(symbolDetails);
                    this.chartingDataService.symbolIdMap.set(symbolDetails.symbol_id.toString(), {
                        symbol_id: symbolDetails.symbol_id,
                        market_id: MarketIDMap[symbolDetails.market_id],
                        security_id: symbolDetails.security_id,
                        is_index: symbolDetails.is_index,
                        scrip_precision: symbolDetails.scrip_precision
                    });
                    if (symbolDetails) {
                        // Check if data is already loaded, if not then subscribe to onLoad
                        if (this.symbolSearchService.symbolsLoaded && this.symbolSearchService.symbolInfoLoaded) {
                            onResolve(this.getSymbolObj(symbolDetails));
                        } else {
                            this.symbolSearchService.onLoad.subscribe((isLoaded: boolean) => {
                                if (isLoaded) {
                                    onResolve(this.getSymbolObj(symbolDetails));
                                }
                            });
                        }
                    }
                });
            } else {
                // Check if data is already loaded, if not then subscribe to onLoad
                if (this.symbolSearchService.symbolsLoaded && this.symbolSearchService.symbolInfoLoaded) {
                    const a = this.getSymbolObj(this.selectedInstrument);
                    onResolve(a);
                } else {
                    this.symbolSearchService.onLoad.subscribe((isLoaded: boolean) => {
                        if (isLoaded) {
                            const a = this.getSymbolObj(this.selectedInstrument);
                            onResolve(a);
                        }
                    });
                }
           }
        }, 0);
    }

    getSymbolObj(symbol: any): any {
        const marketHours = ChartUtilities.defaultMarketMap(this.remoteConfigService.remoteConfigCacheObj['ChartMarketHours']);
        const marketRules = ChartUtilities.processMarketRules(marketHours[MarketIDRevMap[symbol.market_id].toUpperCase()]);
        const symbolName = symbol.scrip_name;
        return {
            name: symbolName,
            description: symbolName,
            ticker: symbol.symbol_id.toString(),
            security_id: symbol.security_id,
            market_id: symbol.market_id,
            type: (chartInstrument[symbol.instrument_type] ?? Instrument.Index),
            session: MarketSessionTimings[MarketIDRevMap[symbol.market_id].toUpperCase()],
            // session: '24x7',
            exchange: MarketIDRevMap[symbol.market_id],
            listed_exchange: MarketIDRevMap[symbol.market_id],
            timezone: 'Asia/Kolkata',
            pricescale: Math.pow(10, symbol.scrip_precision ?? 2),
            minmov: 1,
            supported_resolutions: ['1', '5', '10', '15', '30', '60', '1D', '1W', '1M'] as ResolutionString[],
            currency_code: 'INR',
            has_intraday: true,
            has_weekly_and_monthly: true,
            intraday_multipliers: ['1', '5', '10', '15', '30', '60'],
            data_status: 'streaming',
            has_daily: true,
            format: 'price',
            corrections: marketRules.corrections.join(';'),
            session_holidays: marketRules.session_holidays.join(','),
        };
    }

    getBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: string,
        periodParams: PeriodParams,
        onResult: (bars: Bar[], meta: HistoryMetadata) => void,
        onError: (reason: string) => void,
    ): void {
        this.responseId = Date.now();
        const chartFilters: ChartingFilters = {
            candle_size: this.parseResolutions[resolution],
            symbol_id: this.selectedInstrument.symbol_id,
            from_date: ChartUtilities.translateTVTimestampForMdl(periodParams.from).toString(),
            to_date: ChartUtilities.translateTVTimestampForMdl(periodParams.to).toString(),
            index: this.selectedInstrument.is_index,
            market_id: MarketIDRevMap[this.selectedInstrument.market_id],
            security_id: this.selectedInstrument.security_id,
            trade_value: this.selectedInstrument.trade_value ?? 0,
            market_lot: this.selectedInstrument.market_lot ?? 1,
            scrip_precision: this.selectedInstrument.scrip_precision ?? 2,
            instrument_name: ChartUtilities.getInstrumentName(this.selectedInstrument),
            responseId: this.responseId
        };
        
        if (resolution.endsWith('W') || resolution.endsWith('M')) {
            chartFilters.candle_size = ChartIntervals['1d'];
            chartFilters.from_date = (Date.UTC(2024, 1, 1) / 1000).toString();
        }
        // for now we are not sending the from date as 21 due to less data in algos
        // if (resolution.endsWith('M') ) {
        //     // this is neeed as trading view was giving the from date as an invalid epoch(Jan 1980)
        //     // so added this as per our oldest candle. To be changed if mdl starts giving older ohlc data
        // chartFilters.from_date = (Date.UTC(2021, 1, 1) / 1000).toString();
        // }

        // // Handling for pagination. Requesting data again and again until 10 empty data requests completed
        // // so that any gap in data will be covered.(Same as in chart IQ).
        const nextFromDate = parseInt(chartFilters.from_date, 10)
            // Subtracting 86400 (number of seconds in a day) to extend the date range by one day
            // This ensures we cover any potential gaps in the data and align with daily intervals
            - (86400);
        this.unsuccessfulTries.toDate[symbolInfo.name] = chartFilters.from_date;
        this.unsuccessfulTries.fromDate[symbolInfo.name] = nextFromDate;

        let candle_count = ((+chartFilters.to_date) - (+chartFilters.from_date)) / (MINIMUM_CANDLE_SIZE * CandleIntervals[chartFilters.candle_size]);

        // If candle count is less than MAXIMUM_CANDLE_SIZE_CHARTS then send the request
        if (candle_count < MAXIMUM_CANDLE_SIZE_CHARTS) {
            // this.updateRequestIdCount(data.responseId);
            this.chartingDataService.getChartingData(chartFilters);
            this.ohlcCallBackMap.set(chartFilters.responseId, {
                symbolInfo,
                onError,
                onResult,
                periodParams,
                resolution
            });
        }
        // If candle count is greater than MAXIMUM_CANDLE_SIZE_CHARTS then break the request and then send it
        else {
            // Calculate maximum candle intervals 
            let maxInterval = MAXIMUM_CANDLE_SIZE_CHARTS * (MINIMUM_CANDLE_SIZE * CandleIntervals[chartFilters.candle_size]);
            let from_time = chartFilters.from_date;
            let to_time = chartFilters.to_date;

            // Fetch candle request by breaking timestamps in to multiple parts so that MDL can easily handle the request
            // After each iteration we are passing to_time as a from_date & temp_time as a to_date

            let i = 0;
            while (from_time < to_time) {
                let temp_time = to_time;
                to_time = (Math.max((Number(to_time) - maxInterval), Number(from_time))).toString();
                chartFilters.from_date = to_time;
                chartFilters.to_date = temp_time;
                chartFilters.responseId = chartFilters.responseId + i;
                this.chartingDataService.getChartingData(chartFilters);
                this.ohlcCallBackMap.set(chartFilters.responseId, {
                    symbolInfo,
                    onError,
                    onResult,
                    periodParams,
                    resolution
                });
                i++;
            }
        }


    }

    subscribeBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: string,
        onTick: (bar: Bar) => void,
        listenerGuid: string,
        onResetCacheNeededCallback: () => void
    ): void {
        // setting symbol data in chart service to get quote params if needed
        symbolInfo['symbol_id'] = parseInt(symbolInfo.ticker, 10);
        const symbolData = this.chartingDataService.symbolIdMap.get(symbolInfo.ticker);
        if (symbolData) {
            symbolInfo['market_id'] = symbolData.market_id;
            symbolInfo['security_id'] = symbolData.security_id;
            symbolInfo['scrip_precision'] = symbolData?.scrip_precision ?? 2;
        }
        this._socketSubscriptionMap.set(listenerGuid, this.chartingDataService
            .subscribeCandles(symbolInfo as any, this.parseResolutions[resolution]).subscribe((candle: CandleUpdateObj) => {
                // if (!res.length) {
                //     res = ohlcData2;
                // }
                const res = candle.data;


                if (this.isEmptyBar(res)) {
                    return;
                }

                let volume = res.volume.toString();
                // If resolution is 1D, 1W or 1M, then use the day volume or else handle volume accordingly

                //For now precision is hardcoded to 2 , but to use scrip_precision we need to change the precision in the datafeed
                if ((resolution.endsWith('1D'))) {
                    // we use daily volume  directly 
                    const newBar: Bar = {
                        open: parseFloat(Utils.applyPrecision(res.open_price, 2).toString()),
                        close: parseFloat(Utils.applyPrecision(res.close_price, 2).toString()),
                        volume: parseFloat(volume),
                        // volume: parseFloat((volume).toString()),
                        time: Utils.istToGmt(res.timestamp * 1000),
                        high: Math.max(Utils.applyPrecision(res.close_price, 2), Utils.applyPrecision(res.high_price, 2)),
                        low: Math.min(Utils.applyPrecision(res.close_price, 2), Utils.applyPrecision(res.low_price, 2))
                    };
                    onTick(newBar);
                    return;
                } else if ((this.latestWeeklyMonthlyCandle) && (resolution.endsWith('1W') || resolution.endsWith('1M'))) {
                    // we will append the daily candle to existing week or month volume of latest candle
                    const newBar: Bar = {
                        open: this.latestWeeklyMonthlyCandle?.open ?? parseFloat(Utils.applyPrecision(res.open_price, 2).toString()),
                        close: parseFloat(Utils.applyPrecision(res.close_price, 2).toString()),
                        volume: parseFloat(volume) + this.latestWeeklyMonthlyCandle.volume,
                        // volume: parseFloat((volume).toString()),
                        time: Utils.istToGmt(res.timestamp * 1000),
                        high: Math.max(this.latestWeeklyMonthlyCandle.high, (Utils.applyPrecision(res.high_price, 2))),
                        low: Math.min(this.latestWeeklyMonthlyCandle.low, Utils.applyPrecision(res.low_price, 2))
                    };
                    onTick(newBar);
                    return;
                } else {
                    const firstVolume = this.firstVolumeDict[symbolInfo.ticker];
                    // If first Candle
                    if (!firstVolume) {
                        // If first candle, then set the quote volume = 0
                        volume = this.firstVolumeDict[symbolInfo.ticker] = '0';
                    } else {
                        // Else set the difference between the last and the latest quote
                        if (res.volume > 0) {
                            volume = (parseFloat(res.volume.toString()) - firstVolume).toString();
                        }
                    }
                }

                const bar: Bar = {
                    open: parseFloat(Utils.applyPrecision(res.open_price, 2).toString()),
                    close: parseFloat(Utils.applyPrecision(res.close_price, 2).toString()),
                    volume: parseFloat(volume.toString()),
                    // time: res.timestamp,
                    time: Utils.istToGmt(res.timestamp * 1000),
                    high: parseFloat(Utils.applyPrecision(res.high_price, 2).toString()),
                    low: parseFloat(Utils.applyPrecision(res.low_price, 2).toString())
                };

                // Update the last volume
                this.firstVolumeDict[symbolInfo.ticker] = parseFloat(res.volume.toString());

                onTick(bar);
            }));

        this.onResetCacheCallback = onResetCacheNeededCallback;

    }

    unsubscribeBars(listenerGuid: string): void {
        this._socketSubscriptionMap.get(listenerGuid).unsubscribe();
        this._socketSubscriptionMap.delete(listenerGuid);
    }

    isEmptyBar(candle: ChartDataResponse) {
        return !candle.open_price && !candle.close_price && !candle.high_price && !candle.low_price;
    }

    // subscribeMarketData(symbolId: number, interval: ChartIntervals = ChartIntervals['1h']) {

    //     /**
    //      * Subscribe for Socket Updates
    //      */
    //     // const subscription: SocketGenericMessage = {
    //     //     command: SocketCommands.subscribe,
    //     //     symbolId: symbolId,
    //     //     interval: interval,
    //     // };

    //     // this._sockets.send(SocketConnections.marketData, subscription);
    // }

    // subscribeDepth?(symbolInfo: LibrarySymbolInfo, callback: (data: DOMData) => void): string {
    //     throw new Error('Method not implemented.');
    // }

    // unsubscribeDepth?(subscriberUID: string): void {
    //     throw new Error('Method not implemented.');
    // }


    // getMockData(): ChartData {
    //     // Convert data from {} to []
    //     const arrayOfOHLC = [];
    //     const xAxis: string[] = [];
    //     const volumes: string[] = [];
    //     const allCloses: string[] = [];
    //     const ohlc = [];
    //     ohlcData.sort((a, b) => {
    //         const atime = (new Date(a.timestamp)).getTime();
    //         const btime = (new Date(b.timestamp)).getTime();
    //         return atime - btime;
    //     });
    //     for (let i = 0; i < ohlcData.length; i++) {
    //         const date = new Date(ohlcData[i].timestamp);
    //         // Array of ohlc data (open close low high)
    //         arrayOfOHLC.push([ohlcData[i].open, ohlcData[i].close, ohlcData[i].min, ohlcData[i].max]);

    //         let dateValue = (date.getHours().toString().length < 2 ? '0' : '') + date.getHours().toString();
    //         dateValue += ':';
    //         dateValue += (date.getMinutes().toString().length < 2 ? '0' : '') + date.getMinutes().toString();
    //         // Array of time to show in x axis
    //         xAxis.push(dateValue);

    //         // Array of volumens for volume chart
    //         volumes.push(ohlcData[i].volume);

    //         // Array of close prices
    //         allCloses.push(ohlcData[i].close);

    //         ohlc.push([i, ohlcData[i].open, ohlcData[i].close, ohlcData[i].min, ohlcData[i].max]);
    //     }
    //     return { data: arrayOfOHLC, xAxis: xAxis, volumeData: volumes, allCloses: allCloses, ohlc: ohlc };
    // }

    getQuotes(symbols: string[],
        onDataCallback: (data: (QuoteOkData | QuoteErrorData)[]) => void,
        onErrorCallback: (msg: string) => void
    ): void {
        throw new Error('Method not implemented.');
    }

    subscribeQuotes(symbols: string[],
        fastSymbols: string[], onRealtimeCallback: (data: (QuoteOkData | QuoteErrorData)[]) => void, listenerGUID: string): void {
        throw new Error('Method not implemented.');
    }

    unsubscribeQuotes(listenerGUID: string): void {
        throw new Error('Method not implemented.');
    }

    // Aggregating the daily candles to achieve weekly and monthly candles
    // candles containing daily ohlc data
    // interval specifying weekly and monthly data
    // holidayswith dates to skip
    aggregateCandles(
        candles: any,
        interval: "weekly" | "monthly",
        holidays: string[]
    ): any[] {
        if (!candles.length) return [];

        // Convert holidays to a Set for faster lookup
        const holidaySet = new Set(holidays);


        // aggregated is to hold the final weekly/monthly data
        const aggregated: any[] = [];
        // To Store all the day candles present in a particular week/month
        const grouped: { [key: string]: any[] } = {};

        for (const candle of candles) {
            const date = new Date(candle.time);

            // Skip Saturdays and Sundays
            const dayOfWeek = date.getUTCDay();
            if (dayOfWeek === 0 || dayOfWeek === 6) continue; // 0 = Sunday, 6 = Saturday

            // Format the date as "YYYYMMDD" and skip if it's a holiday
            const dateStr = date.toISOString().slice(0, 10).replace(/-/g, "");
            if (holidaySet.has(dateStr)) {
                continue;
            }

            let key: string;

            // to find the key which corresponds to the current week/month of the candle being processed
            if (interval === "weekly") {
                const startOfWeek = new Date(date);
                startOfWeek.setUTCDate(
                    date.getUTCDate() - ((date.getUTCDay() + 6) % 7)
                ); // Start of the week (Monday)
                startOfWeek.setUTCHours(0, 0, 0, 0);
                key = startOfWeek.toISOString();
            } else if (interval === "monthly") {
                const startOfMonth = new Date(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0);
                startOfMonth.setUTCHours(0, 0, 0, 0);
                key = startOfMonth.toISOString();
            } else {
                throw new Error("Unsupported interval");
            }

            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(candle);
        }

        // pushing the candle data of the group to the final data based on OHLC of the week/month candle
        for (const key in grouped) {
            const timestamp = new Date(key);
            const group = grouped[key];
            const high = Math.max(...group.map(c => c.high));
            const low = Math.min(...group.map(c => c.low));
            const open = group[0].open;
            const close = group[group.length - 1].close;
            const volume = group.reduce((sum, c) => sum + c.volume, 0);
            const time = new Date(timestamp.getTime() + (24 * 60 * 60 * 1000)).getTime();

            aggregated.push({ close, high, low, open, time, volume });
        }

        // to store weekly and monthly latest candle
        this.latestWeeklyMonthlyCandle = {
            open: parseFloat(aggregated[aggregated.length - 1].open.toString()),
            close: parseFloat(aggregated[aggregated.length - 1].close.toString()),
            volume: parseFloat(aggregated[aggregated.length - 1].volume.toString()),
            time: aggregated[aggregated.length - 1].time,
            high: parseFloat(aggregated[aggregated.length - 1].high.toString()),
            low: parseFloat(aggregated[aggregated.length - 1].low.toString())
        };

        return aggregated;
    }
}
